import {
  ContextMenu,
  DragHandle as DragHandleIcon,
  Tooltip,
  useDraggable,
} from '@affine/component';
import {
  AnimatedSelectionSlot,
  applyRangeSelection,
  replaceSelectionKey,
  SelectionCheckbox,
  toggleSelectionKey,
} from '@affine/core/components/selection-checkbox';
import { PublicUserLabel } from '@affine/core/modules/cloud/views/public-user';
import { type DocRecord, DocsService } from '@affine/core/modules/doc';
import { DocDisplayMetaService } from '@affine/core/modules/doc-display-meta';
import { WorkspacePresenceStore } from '@affine/core/modules/organize';
import { WorkbenchLink } from '@affine/core/modules/workbench';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import {
  AutoTidyUpIcon,
  PropertyIcon,
  ResizeTidyUpIcon,
} from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type HTMLProps,
  memo,
  type MouseEvent as ReactMouseEvent,
  type MouseEventHandler,
  type ReactNode,
  type SVGProps,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { PagePreview } from '../../page-list/page-content-preview';
import { DocExplorerContext } from '../context';
import { quickActions } from '../quick-actions.constants';
import * as styles from './doc-list-item.css';
import { MoreMenuButton, MoreMenuContent } from './more-menu';
import { CardViewProperties, ListViewProperties } from './properties';

export type DocListItemView = 'list' | 'grid' | 'masonry';
export type ListViewSelectionVariant = 'overlay' | 'inline';

export const DocListViewIcon = ({
  view,
  ...props
}: { view: DocListItemView } & SVGProps<SVGSVGElement>) => {
  const Component = {
    list: PropertyIcon,
    grid: ResizeTidyUpIcon,
    masonry: AutoTidyUpIcon,
  }[view];

  return <Component {...props} />;
};

export interface DocListItemProps {
  docId: string;
  groupId: string;
  listViewProperties?: ReactNode;
  listViewActions?: ReactNode;
  listViewSelectionVariant?: ListViewSelectionVariant;
}

export class MixId {
  static connector = '||';
  static create(groupId: string, docId: string) {
    return `${groupId}${this.connector}${docId}`;
  }
  static parse(mixId: string) {
    if (!mixId) {
      return { groupId: null, docId: null };
    }
    const [groupId, docId] = mixId.split(this.connector);
    return { groupId, docId };
  }
}

function getExplorerOrderedKeys(
  selectableItems: { groupId: string; itemId: string }[],
  groups: { key: string; items: string[] }[]
) {
  return selectableItems.length > 0
    ? selectableItems.map(item => MixId.create(item.groupId, item.itemId))
    : groups.flatMap(group =>
        group.items.map(docId => MixId.create(group.key, docId))
      );
}

function getDocSelectionKeyFromMixId(mixId: string) {
  return MixId.parse(mixId).docId;
}

export const DocListItem = ({ ...props }: DocListItemProps) => {
  const contextValue = useContext(DocExplorerContext);
  const view = useLiveData(contextValue.view$) ?? 'list';
  const groups = useLiveData(contextValue.groups$);
  const selectableItems = useLiveData(contextValue.selectableItems$);
  const selectMode = useLiveData(contextValue.selectMode$);
  const selectedDocIds = useLiveData(contextValue.selectedDocIds$);
  const prevCheckAnchorId = useLiveData(contextValue.prevCheckAnchorId$);
  const allowDrag = useLiveData(contextValue.allowDrag$) !== false;

  const handleMultiSelect = useCallback(
    (prevCursor: string | null, currCursor: string) => {
      const result = applyRangeSelection({
        anchorKey: prevCursor,
        orderedKeys: getExplorerOrderedKeys(selectableItems, groups),
        selectedKeys: contextValue.selectedDocIds$?.value ?? [],
        targetKey: currCursor,
        getSelectionKey: getDocSelectionKeyFromMixId,
      });
      contextValue.selectedDocIds$?.next(result.selectedKeys);
      contextValue.prevCheckAnchorId$?.next(result.anchorKey);
    },
    [contextValue, groups, selectableItems]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<Element>) => {
      const { docId, groupId } = props;
      const currCursor = MixId.create(groupId, docId);
      if (selectMode || e.shiftKey) {
        e.preventDefault();
      }

      if (selectMode) {
        if (e.shiftKey) {
          handleMultiSelect(prevCheckAnchorId ?? null, currCursor);
        } else {
          const result = toggleSelectionKey({
            selectedKeys: contextValue.selectedDocIds$.value,
            targetKey: docId,
          });
          contextValue.selectedDocIds$?.next(result.selectedKeys);
          contextValue.prevCheckAnchorId$?.next(currCursor);
        }
      } else {
        if (e.shiftKey) {
          const result = replaceSelectionKey(docId);
          contextValue.selectMode$?.next(true);
          contextValue.selectedDocIds$?.next(result.selectedKeys);
          contextValue.prevCheckAnchorId$?.next(currCursor);
          return;
        } else {
          // as link
          track.allDocs.list.doc.openDoc();
          return;
        }
      }
    },
    [contextValue, handleMultiSelect, prevCheckAnchorId, props, selectMode]
  );

  const { dragRef, CustomDragPreview } = useDraggable<AffineDNDData>(
    () => ({
      canDrag: allowDrag,
      data: {
        entity: {
          type: 'doc',
          id: props.docId as string,
        },
        from: {
          at: 'all-docs:list',
        },
      },
    }),
    [allowDrag, props.docId]
  );

  return (
    <>
      <WorkbenchLink
        ref={dragRef}
        draggable={false}
        to={`/${props.docId}`}
        onClick={handleClick}
        data-selected={selectedDocIds.includes(props.docId)}
        className={styles.root}
        data-testid={`doc-list-item`}
        data-doc-id={props.docId}
      >
        {view === 'list' ? (
          <ListViewDoc {...props} />
        ) : (
          <CardViewDoc {...props} />
        )}
      </WorkbenchLink>
      <CustomDragPreview>
        <div className={styles.dragPreview}>
          <RawDocIcon id={props.docId} className={styles.dragPreviewIcon} />
          <RawDocTitle id={props.docId} />
        </div>
      </CustomDragPreview>
    </>
  );
};

const RawDocIcon = memo(function RawDocIcon({
  id,
  ...props
}: HTMLProps<SVGSVGElement>) {
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const Icon = useLiveData(id ? docDisplayMetaService.icon$(id) : null);
  return <Icon {...props} />;
});
const RawDocTitle = memo(function RawDocTitle({ id }: { id: string }) {
  const docDisplayMetaService = useService(DocDisplayMetaService);
  const title = useLiveData(docDisplayMetaService.title$(id));
  return title;
});
const RawDocPreview = memo(function RawDocPreview({
  id,
  loading,
}: {
  id: string;
  loading?: ReactNode;
}) {
  return <PagePreview pageId={id} fallback={loading} />;
});

const EMPTY_PRESENCE_USER_IDS: string[] = [];
const MAX_VISIBLE_PRESENCE_AVATARS = 4;

const DocLivePresence = memo(function DocLivePresence({
  docId,
}: {
  docId: string;
}) {
  const presenceStore = useService(WorkspacePresenceStore);
  const docPresence$ = useMemo(
    () =>
      presenceStore.docPresence$.selector(
        presenceMap => presenceMap.get(docId) ?? EMPTY_PRESENCE_USER_IDS
      ),
    [docId, presenceStore]
  );
  const userIds = useLiveData(docPresence$);

  if (userIds.length === 0) {
    return null;
  }

  const visible = userIds.slice(0, MAX_VISIBLE_PRESENCE_AVATARS);
  const overflowCount = userIds.length - visible.length;

  return (
    <div className={styles.livePresenceStack}>
      {visible.map(userId => (
        <div key={userId} className={styles.livePresenceAvatar}>
          <PublicUserLabel id={userId} size={18} showName={false} />
        </div>
      ))}
      {overflowCount > 0 && (
        <div className={styles.livePresenceOverflow}>+{overflowCount}</div>
      )}
    </div>
  );
});
const DragHandle = memo(function DragHandle({
  id,
  ...props
}: HTMLProps<HTMLDivElement>) {
  const contextValue = useContext(DocExplorerContext);
  const selectMode = useLiveData(contextValue.selectMode$);
  const showDragHandle = useLiveData(contextValue.showDragHandle$);
  const allowDrag = useLiveData(contextValue.allowDrag$) !== false;

  if (selectMode || !id || !showDragHandle || !allowDrag) {
    return null;
  }

  return (
    <div {...props}>
      <DragHandleIcon />
    </div>
  );
});
const Select = memo(function Select({
  id,
  variant = 'overlay',
  ...props
}: HTMLProps<HTMLDivElement> & { variant?: ListViewSelectionVariant }) {
  const contextValue = useContext(DocExplorerContext);
  const groups = useLiveData(contextValue.groups$);
  const selectMode = useLiveData(contextValue.selectMode$);
  const selectableItems = useLiveData(contextValue.selectableItems$);
  const selectedDocIds = useLiveData(contextValue.selectedDocIds$);
  const prevCheckAnchorId = useLiveData(contextValue.prevCheckAnchorId$);
  const selected = id ? selectedDocIds.includes(id) : false;

  const handleSelectChange = useCallback(
    (event?: ReactMouseEvent) => {
      if (!id) return;
      const item = contextValue.selectableItems$.value.find(
        item => item.itemId === id
      );
      const currCursor = item ? MixId.create(item.groupId, id) : id;
      contextValue.selectMode$?.next(true);

      if (event?.shiftKey) {
        const result = applyRangeSelection({
          anchorKey: prevCheckAnchorId,
          orderedKeys: getExplorerOrderedKeys(selectableItems, groups),
          selectedKeys: contextValue.selectedDocIds$.value,
          targetKey: currCursor,
          getSelectionKey: getDocSelectionKeyFromMixId,
        });
        contextValue.selectedDocIds$?.next(result.selectedKeys);
        contextValue.prevCheckAnchorId$?.next(result.anchorKey);
        return;
      }

      const result = toggleSelectionKey({
        selectedKeys: contextValue.selectedDocIds$.value,
        targetKey: id,
      });
      contextValue.selectedDocIds$?.next(result.selectedKeys);
      contextValue.prevCheckAnchorId$?.next(currCursor);
    },
    [contextValue, groups, id, prevCheckAnchorId, selectableItems]
  );

  if (!id) {
    return null;
  }

  if (variant === 'inline') {
    return (
      <AnimatedSelectionSlot
        checked={selected}
        visible={Boolean(selectMode || selected)}
        gap={0}
        hiddenMarginRight={-8}
        data-testid={`doc-list-item-select`}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          handleSelectChange(e);
        }}
        checkboxProps={{ style: { pointerEvents: 'none' } }}
        {...props}
      />
    );
  }

  return (
    <div
      data-select-mode={selectMode}
      data-testid={`doc-list-item-select`}
      {...props}
    >
      <SelectionCheckbox
        checked={selected}
        onChange={() => handleSelectChange()}
      />
    </div>
  );
});
// Different with RawDocIcon, refer to `ExplorerDisplayPreference.showDocIcon`
const DocIcon = memo(function DocIcon({
  id,
  ...props
}: HTMLProps<HTMLDivElement>) {
  const contextValue = useContext(DocExplorerContext);
  const showDocIcon = useLiveData(contextValue.showDocIcon$);
  if (!showDocIcon) {
    return null;
  }
  return (
    <div {...props}>
      <RawDocIcon id={id} />
    </div>
  );
});
const DocTitle = memo(function DocTitle({
  id,
  ...props
}: HTMLProps<HTMLDivElement>) {
  if (!id) return null;
  return (
    <div {...props}>
      <RawDocTitle id={id} />
    </div>
  );
});
const DocPreview = memo(function DocPreview({
  id,
  loading,
  ...props
}: HTMLProps<HTMLDivElement> & { loading?: ReactNode }) {
  const contextValue = useContext(DocExplorerContext);
  const showDocPreview = useLiveData(contextValue.showDocPreview$);

  if (!id || !showDocPreview) return null;

  return (
    <div {...props}>
      <RawDocPreview id={id} loading={loading} />
    </div>
  );
});

/**
 * Generic document-list row layout.
 * Renders the standard list view structure with configurable slots.
 */
export interface ListRowProps {
  /** Slot before the icon (e.g. expand button for folders) */
  beforeIcon?: ReactNode;
  /** Icon slot (24x24) */
  icon: ReactNode;
  /** Title slot */
  title: ReactNode;
  /** Preview slot (below title) */
  preview?: ReactNode;
  /** Properties slot (between spacer and actions) */
  properties?: ReactNode;
  /** Action buttons slot */
  actions?: ReactNode;
  /** Context menu items (wraps in ContextMenu when provided) */
  contextMenuItems?: ReactNode;
  /** DragHandle slot */
  dragHandle?: ReactNode;
  /** Select checkbox slot */
  select?: ReactNode;
  /** Row click handler for non-doc rows that reuse the list layout */
  onClick?: MouseEventHandler<HTMLLIElement>;
  /** selected state for list rows that do not use the doc wrapper */
  'data-selected'?: boolean;
  /** data-testid for the row */
  'data-testid'?: string;
  /** data item id for keyboard selection scroll targets */
  'data-item-id'?: string;
}
export const ListRow = ({
  beforeIcon,
  icon,
  title,
  preview,
  properties,
  actions,
  contextMenuItems,
  dragHandle,
  select,
  onClick,
  ...rest
}: ListRowProps) => {
  const content = (
    <li
      className={styles.listViewRoot}
      data-testid={rest['data-testid']}
      data-item-id={rest['data-item-id']}
      data-selected={rest['data-selected']}
      onClick={onClick}
    >
      {dragHandle}
      {select}
      {beforeIcon}
      <div className={styles.listIcon}>{icon}</div>
      <div className={styles.listBrief}>
        <div className={styles.listTitle} data-testid="doc-list-item-title">
          {title}
        </div>
        {preview && <div className={styles.listPreview}>{preview}</div>}
      </div>
      <div className={styles.listSpace} />
      {properties}
      {actions}
    </li>
  );

  if (contextMenuItems) {
    return (
      <ContextMenu asChild items={contextMenuItems}>
        {content}
      </ContextMenu>
    );
  }
  return content;
};

/**
 * Generalized list-view item.
 * Provides the same DragHandle / Select / ListRow layout for explorer items.
 */
export interface ListViewItemProps {
  /** ID used by DragHandle and Select — controls visibility via DocExplorerContext */
  id: string;
  /** Slot before the icon (e.g. expand button for folders) */
  beforeIcon?: ReactNode;
  /** Icon content (24×24) */
  icon: ReactNode;
  /** Title content */
  title: ReactNode;
  /** Preview below title */
  preview?: ReactNode;
  /** Properties area (between spacer and actions) */
  properties?: ReactNode;
  /** Action buttons */
  actions?: ReactNode;
  /** Context menu items */
  contextMenuItems?: ReactNode;
  /** data-testid */
  'data-testid'?: string;
  /** Selection presentation variant for list rows */
  selectionVariant?: ListViewSelectionVariant;
}

export const ListViewItem = ({
  id,
  beforeIcon,
  icon,
  title,
  preview,
  properties,
  actions,
  contextMenuItems,
  selectionVariant = 'overlay',
  ...rest
}: ListViewItemProps) => {
  return (
    <ListRow
      dragHandle={<DragHandle id={id} className={styles.listDragHandle} />}
      select={
        <Select
          id={id}
          variant={selectionVariant}
          className={
            selectionVariant === 'inline'
              ? styles.listInlineSelect
              : styles.listSelect
          }
        />
      }
      beforeIcon={beforeIcon}
      icon={icon}
      title={title}
      preview={preview}
      properties={properties}
      contextMenuItems={contextMenuItems}
      actions={actions}
      data-testid={rest['data-testid']}
      data-item-id={id}
    />
  );
};

const listMoreMenuContentOptions = {
  side: 'bottom',
  align: 'end',
  sideOffset: 12,
  alignOffset: -4,
} as const;
export const ListViewDoc = ({
  docId,
  listViewProperties,
  listViewActions,
  listViewSelectionVariant,
}: DocListItemProps) => {
  const t = useI18n();
  const docsService = useService(DocsService);
  const doc = useLiveData(docsService.list.doc$(docId));
  const contextValue = useContext(DocExplorerContext);
  const showMoreOperation = useLiveData(contextValue.showMoreOperation$);

  if (!doc) {
    return null;
  }

  return (
    <ListViewItem
      id={docId}
      icon={<RawDocIcon id={docId} />}
      title={<RawDocTitle id={docId} />}
      preview={<DocPreview id={docId} className={styles.listPreview} />}
      properties={
        listViewProperties === undefined ? (
          <>
            <DocLivePresence docId={docId} />
            <ListViewProperties docId={docId} />
          </>
        ) : (
          listViewProperties
        )
      }
      contextMenuItems={
        showMoreOperation ? <MoreMenuContent docId={docId} /> : undefined
      }
      actions={
        listViewActions === undefined ? (
          <>
            {quickActions.map(action => (
              <Tooltip key={action.key} content={t.t(action.name)}>
                <action.Component doc={doc} />
              </Tooltip>
            ))}
            <MoreMenuButton
              docId={docId}
              contentOptions={listMoreMenuContentOptions}
            />
          </>
        ) : (
          listViewActions
        )
      }
      selectionVariant={listViewSelectionVariant}
    />
  );
};

const cardMoreMenuContentOptions = {
  side: 'bottom',
  align: 'end',
  sideOffset: 12,
  alignOffset: -4,
} as const;

export const CardViewDoc = ({ docId }: DocListItemProps) => {
  const t = useI18n();
  const contextValue = useContext(DocExplorerContext);
  const selectMode = useLiveData(contextValue.selectMode$);
  const docsService = useService(DocsService);
  const rawDoc = useLiveData(docsService.list.doc$(docId));
  const showMoreOperation = useLiveData(contextValue.showMoreOperation$);

  if (!rawDoc) {
    return null;
  }
  const doc = rawDoc as DocRecord;

  return (
    <ContextMenu
      asChild
      disabled={!showMoreOperation}
      items={<MoreMenuContent docId={docId} />}
    >
      <li className={styles.cardViewRoot}>
        <DragHandle id={docId} className={styles.cardDragHandle} />
        <header className={styles.cardViewHeader}>
          <DocIcon id={docId} className={styles.cardViewIcon} />
          <DocTitle
            id={docId}
            className={styles.cardViewTitle}
            data-testid="doc-list-item-title"
          />
          <DocLivePresence docId={docId} />
          {quickActions.map(action => {
            const Action = action.Component as React.FC<any>;
            return (
              <Tooltip key={action.key} content={t.t(action.name)}>
                <Action size="16" doc={doc} />
              </Tooltip>
            );
          })}
          {selectMode ? (
            <Select id={docId} className={styles.cardViewCheckbox} />
          ) : (
            <MoreMenuButton
              docId={docId}
              contentOptions={cardMoreMenuContentOptions}
              iconProps={{ size: '16' }}
            />
          )}
        </header>
        <DocPreview id={docId} className={styles.cardPreviewContainer} />
        <CardViewProperties docId={docId} />
      </li>
    </ContextMenu>
  );
};
